/**
 * Revenue Calculation — pure metric helpers shared by the KPI row (card 1242), the
 * product table (1243), the cadence timeline (1244) and the panels (1245).
 *
 * Every function here is PURE: same input → same output, no effects, no throws.
 * They live in utils rather than in a component because four cards render the same
 * money figures and a second formatting rule is exactly how "$0.00" quietly replaces
 * "unknown" on one surface but not another.
 *
 * 🔑 THE ONE RULE THIS FILE ENFORCES: a blocked money input renders `— / Missing`,
 * never $0, never 0%, never a 100% margin. The economics API encodes "blocked" as
 * `value: null` on the metric object `{ value, basis, source, dataQuality, blockedBy }`,
 * so "is this blocked?" is exactly "is the value a finite number?" — see
 * api/services/revenueEconomicsService.js and docs/oval/project-oval-revenue/README.md.
 */

import { formatPrice } from './formatters';

/** The literal a blocked money input renders as. Never "$0.00", never "0%". */
export const MISSING_TEXT = '— / Missing';

/**
 * What a blocked figure reads as, BY the reason it is blocked. A gap and a contradiction
 * are different facts and the page must not flatten them: `missing` means nobody recorded
 * the number, `conflicting` means two sources disagree and this page refuses to pick a
 * winner (card 1243 criterion 7 — a DoctorNow `supply_pricing_meta.ourCost`, blocker Q1).
 */
export const BLOCKED_TEXT = Object.freeze({
  missing: MISSING_TEXT,
  conflicting: '— / Conflicting'
});

/** Rendered where a product does not sell a term at all. NOT a blocker — see NOT_SOLD_TEXT. */
export const NOT_SOLD_TEXT = 'Not sold';

/** The two data qualities that REFUSE arithmetic. "Incomplete" is their OR. */
export const INCOMPLETE_QUALITIES = Object.freeze(['missing', 'conflicting']);

/**
 * Quality severity, mirroring revenueEconomicsService.js:33. A derived figure takes the
 * WORST quality of its inputs, so one conflicting cost poisons the profit that reads it —
 * which is the intended behaviour, not a bug.
 */
const SEVERITY = Object.freeze({ verified: 0, estimated: 1, missing: 2, conflicting: 3 });

/** PURE. 'verified' and 'estimated' may compute; 'missing' and 'conflicting' refuse. */
const isResolvableQuality = (quality) => SEVERITY[quality] < SEVERITY.missing;

/** PURE. The worst quality among the given metrics. Mirrors revenueEconomicsService.js:38. */
export const worstQuality = (metrics = []) =>
  metrics.reduce(
    (acc, m) => (SEVERITY[m?.dataQuality] > SEVERITY[acc] ? m.dataQuality : acc),
    'verified'
  );

/**
 * PURE. A metric is blocked unless its value is a genuine finite NUMBER.
 * `null` (the API's blocked signal), undefined and NaN are all blocked; 0 is NOT —
 * a real, measured zero is a fact and must keep rendering as $0.00.
 *
 * ⛔ DO NOT REWRITE THIS AS `!Number.isFinite(Number(metric?.value))`. It was written
 * that way first and it SHIPPED THE EXACT BUG THIS PROJECT EXISTS TO PREVENT:
 * `Number(null) === 0`, which is finite, so every blocked input rendered `$0.00`
 * instead of `— / Missing` — net settled revenue read as $0.00 and fully-loaded margin
 * as 0.0%. Measured on live before the fix. The `typeof` test is the whole guard:
 * it rejects null/undefined/'' by TYPE, before any numeric coercion can invent a zero.
 */
export const isBlockedMetric = (metric) =>
  typeof metric?.value !== 'number' || !Number.isFinite(metric.value);

/**
 * PURE. Blocked → the text for WHY it is blocked. Money arrives in CENTS; a margin
 * arrives as a RATIO. Both come straight off the API metric — this never derives a
 * figure of its own. An unknown/absent quality falls back to MISSING_TEXT, which is
 * what `formatMetricValue(null, …)` relies on.
 */
export const formatMetricValue = (metric, kind) =>
  (isBlockedMetric(metric)
    ? (BLOCKED_TEXT[metric?.dataQuality] ?? MISSING_TEXT)
    : (kind === 'percent'
      ? `${(Number(metric.value) * 100).toFixed(1)}%`
      : formatPrice(Number(metric.value))));

/** PURE. The tooltip behind every figure: what it was computed from, and from where. */
export const describeMetricBasis = (metric) =>
  [metric?.basis, metric?.source && metric.source !== 'none' ? `source: ${metric.source}` : null,
    metric?.blockedBy ? `blocked by ${metric.blockedBy}` : null]
    .filter(Boolean)
    .join(' — ');

/**
 * PURE. An exact mirror of revenueEconomicsService.js:192 `subtractMetrics`, so the two
 * columns this page derives client-side obey the SAME arithmetic rule as every figure the
 * server derived. ⛔ Do not "simplify" this to `a.value - b.value` — the whole point is
 * that a blocked or conflicting input propagates instead of silently contributing zero.
 */
export const subtractMetrics = (left, right, basis) => {
  const dataQuality = worstQuality([left, right]);
  return {
    value: isResolvableQuality(dataQuality) ? Number(left?.value) - Number(right?.value) : null,
    basis,
    source: 'derived',
    dataQuality,
    blockedBy: [left, right].find((m) => !isResolvableQuality(m?.dataQuality))?.blockedBy ?? null
  };
};

/**
 * PURE. Mirrors revenueCalculationController.js:121 `toMargin` — a margin refuses to
 * resolve on a non-finite numerator or a non-positive denominator, so a zero-revenue row
 * can never print "0.0%" as though it were measured.
 */
export const deriveMargin = (profitMetric, grossMetric, basis) => {
  const dataQuality = worstQuality([profitMetric, grossMetric]);
  const gross = Number(grossMetric?.value);
  const profit = Number(profitMetric?.value);
  const resolved = isResolvableQuality(dataQuality) && Number.isFinite(profit) && gross > 0;
  return {
    value: resolved ? profit / gross : null,
    basis,
    source: 'derived',
    dataQuality: resolved || !isResolvableQuality(dataQuality) ? dataQuality : 'missing',
    blockedBy: [profitMetric, grossMetric].find((m) => !isResolvableQuality(m?.dataQuality))?.blockedBy ?? null
  };
};

/** PURE. contributionProfit / grossRevenue. `row.profit` IS the contribution profit. */
export const deriveContributionMargin = (row) =>
  deriveMargin(row?.profit, row?.grossRevenue, 'contributionProfit / grossRevenue');

/** PURE. contributionProfit - allocated static provider expense (blocked on Q5 today). */
export const deriveFullyLoadedProfit = (row) =>
  subtractMetrics(row?.profit, row?.staticProviderExpenseAllocated,
    'contributionProfit - staticProviderExpenseAllocated');

/** PURE. fullyLoadedProfit / grossRevenue. */
export const deriveFullyLoadedMargin = (row) =>
  deriveMargin(deriveFullyLoadedProfit(row), row?.grossRevenue, 'fullyLoadedProfit / grossRevenue');

/**
 * PURE. Applies the catalog-scope filters the ENDPOINT CANNOT EXPRESS.
 *
 * Provider is deliberately absent: `/admin/revenue-calculation/products` models it 1:1
 * and applies it server-side. These three are here because the endpoint cannot say them —
 * `dataQuality` is an exact match so it cannot mean "missing OR conflicting", and
 * `includeInactive` is include-or-exclude so it cannot mean "inactive ONLY".
 */
export const filterCatalogRows = (rows, values = {}) =>
  (rows ?? []).filter((row) => {
    if (values.category && row?.category !== values.category) return false;
    // Blocker selection composes with the other filters rather than short-circuiting them,
    // so picking Q1 inside an already-filtered category narrows, never widens.
    if (values.blocker && !(row?.blockedBy ?? []).includes(values.blocker)) return false;
    if (values.active === 'active' && !row?.isActive) return false;
    if (values.active === 'inactive' && row?.isActive) return false;
    if (values.economics === 'incomplete') return INCOMPLETE_QUALITIES.includes(row?.dataQuality);
    if (values.economics) return row?.dataQuality === values.economics;
    return true;
  });

/**
 * PURE. Flat product × dosage × term rows → one group per product, input order preserved.
 *
 * 🔑 The API returns 565 rows for 64 products — one per product × dosage × term. The table
 * shows one EXPANDABLE row per product, so the grouping is the table's spine. `termRowCount`
 * is carried on the group precisely so "nothing was dropped" is measurable from the collapsed
 * table: the sum of every group's count must equal the API's own `count`.
 */
export const groupRowsByProduct = (rows = []) => {
  const order = [];
  const groups = new Map();
  (rows ?? []).forEach((row) => {
    const id = row?.productId ?? 'unknown';
    if (!groups.has(id)) {
      groups.set(id, []);
      order.push(id);
    }
    groups.get(id).push(row);
  });
  return order.map((productId) => {
    const termRows = groups.get(productId);
    const [first] = termRows;
    return {
      productId,
      productName: first?.productName ?? 'Unnamed product',
      category: first?.category ?? null,
      providerType: first?.providerType ?? null,
      isActive: first?.isActive !== false,
      dosageCount: new Set(termRows.map((r) => r.dosageId)).size,
      offeredTermLabels: [...new Set(termRows.filter((r) => r?.term?.isOffered).map((r) => r.term.label))],
      dataQuality: worstQuality(termRows),
      blockedBy: [...new Set(termRows.flatMap((r) => r?.blockedBy ?? []))],
      termRowCount: termRows.length,
      termRows
    };
  });
};

/* --------------------------------------------- cadence timeline helpers (card 1244) */

/**
 * PURE. The fills of one term, as the timeline renders them.
 *
 * ⛔ `Math.ceil` IS NOT RECOMPUTED HERE. The shipment count and the months in each
 * shipment are read straight off `row.cadence`, which the server built by calling
 * `supplyTermService.getShipments` / `calculateFulfillmentSchedule`. A second ceil in
 * the view is a second answer, and the whole point of this page is that there is one.
 * This function only pairs each shipment with its ordinal and its running start month.
 */
export const buildCadenceFills = (cadence) => {
  const months = Array.isArray(cadence?.shipmentMonths) ? cadence.shipmentMonths : [];
  return months.reduce((fills, span, index) => {
    const startMonth = fills.reduce((sum, fill) => sum + fill.months, 0) + 1;
    return [...fills, { ordinal: index + 1, months: span, startMonth, endMonth: startMonth + span - 1 }];
  }, []);
};

/**
 * PURE. The medication price basis in the plan's vocabulary, with its quantity.
 * `basisKind` comes from the SERVER (revenueTitration.js MEDICATION_BASIS) because the
 * cost builder is what knows which branch it took; reading it back out of a prose
 * `basis` string in the view would be a guess that breaks the day the wording changes.
 */
export const describeMedicationBasis = (metric) => {
  const kind = metric?.basisKind;
  if (!kind) return 'basis not recorded';
  const quantity = metric?.basisQuantity;
  return typeof quantity === 'number' ? `${kind} × ${quantity}` : kind;
};

/**
 * PURE. The titration ladder for ONE supply term: every dosage step of the product with
 * that term's economics attached, in catalog `display_order`.
 *
 * `costChanged` marks the steps where the medication figure differs from the step below
 * it — "the point at which cost changes" in §Cadence model. A step whose cost is BLOCKED
 * is compared by its blocked-ness, not by a number, so `null` vs `null` is not a change
 * and `null` vs `$270.00` is. Step 0 can never be a change: there is nothing beneath it.
 */
export const buildTitrationLadder = (titration = [], termKey = null) =>
  [...(Array.isArray(titration) ? titration : [])]
    .map((step) => ({
      dosageId: step?.dosageId ?? null,
      dosageAmount: step?.dosageAmount ?? null,
      consultation: step?.consultation ?? null,
      row: (step?.terms ?? []).find((term) => term?.term?.key === String(termKey)) ?? null
    }))
    .sort((a, b) => (a.row?.dosage?.stepOrder ?? 0) - (b.row?.dosage?.stepOrder ?? 0))
    .map((step, index, steps) => {
      const previous = index > 0 ? steps[index - 1].row?.medicationCost : undefined;
      const current = step.row?.medicationCost;
      return {
        ...step,
        costChanged: index > 0
          && (isBlockedMetric(previous) !== isBlockedMetric(current)
            || (!isBlockedMetric(current) && previous?.value !== current?.value))
      };
    });

/** PURE. The term keys a product actually sells, in the API order, deduplicated. */
export const listOfferedTerms = (titration = []) => {
  const seen = new Map();
  (Array.isArray(titration) ? titration : []).forEach((step) => {
    (step?.terms ?? []).forEach((row) => {
      if (row?.term?.isOffered && !seen.has(row.term.key)) seen.set(row.term.key, row.term.label);
    });
  });
  return [...seen].map(([key, label]) => ({ key, label }));
};
